import type { OnboardingDataItemType } from "@memorang/constants/config/schema";
import { useTranslation } from "react-i18next";
import { List, Text } from "react-native-paper";
import { useAppTheme } from "../../useAppTheme";
import { AppIcon } from "../AppIcon";
import Box from "../Box";
import { useOnboarding } from "./OnboardingProvider";
import { OnboardingBox } from "./components/OnboardingBox";
import { resolveI18n } from "./helpers";

type Props = {
	title: string;
	description: string | undefined;
	data: OnboardingDataItemType[] | undefined;
};

type LearnerTypeItemProps = {
	item: OnboardingDataItemType;
	isSelected: boolean;
	locale: string;
	onPress: () => void;
};

const LearnerTypeItem = ({
	item,
	isSelected,
	locale,
	onPress,
}: LearnerTypeItemProps) => {
	const theme = useAppTheme();
	const title = resolveI18n(item.titlei18n, locale);
	const description = resolveI18n(item.descriptioni18n, locale);

	return (
		<List.Item
			onPress={onPress}
			style={{
				borderWidth: 1,
				borderColor: isSelected
					? theme.colors.secondary
					: theme.colors.surfaceVariant,
				borderRadius: theme.roundness * 3,
				backgroundColor: isSelected
					? theme.colors.elevation.level1
					: theme.colors.surface,
				padding: 12,
			}}
			contentStyle={{ gap: 2 }}
			left={() => (
				<Box
					style={{
						width: 36,
						height: 36,
						borderRadius: 99,
						backgroundColor: isSelected
							? theme.colors.elevation.level5
							: theme.colors.background,
						justifyContent: "center",
						alignItems: "center",
						alignSelf: "center",
					}}
				>
					<AppIcon
						name={(item.icon ?? "help-circle-outline") as never}
						size={22}
						color={
							isSelected ? theme.colors.secondary : theme.colors.inverseSurface
						}
					/>
				</Box>
			)}
			title={title}
			titleStyle={{ fontWeight: "600" }}
			description={description}
			descriptionStyle={{ color: theme.colors.onSurfaceVariant }}
		/>
	);
};

export const OnboardingLearnerTypeScreen = ({
	title,
	description,
	data = [],
}: Props) => {
	const { selectedLearnerTypeId, onSelectLearnerType } = useOnboarding();
	const theme = useAppTheme();
	const { i18n } = useTranslation();
	const locale = i18n.language;

	return (
		<Box flex={1} gap={16}>
			<OnboardingBox>
				<Box gap={14}>
					<Text variant="titleLarge">{title}</Text>
					{description ? (
						<Text
							variant="bodyMedium"
							style={{ color: theme.colors.onSurfaceVariant }}
						>
							{description}
						</Text>
					) : null}
				</Box>
				<Box gap={12}>
					{data.map((item) => (
						<LearnerTypeItem
							key={item.type ?? resolveI18n(item.titlei18n, "en-US") ?? ""}
							item={item}
							locale={locale}
							isSelected={
								selectedLearnerTypeId ===
								(item.type ?? resolveI18n(item.titlei18n, "en-US") ?? "")
							}
							onPress={() =>
								onSelectLearnerType(
									item.type ?? resolveI18n(item.titlei18n, "en-US") ?? "",
								)
							}
						/>
					))}
				</Box>
			</OnboardingBox>
		</Box>
	);
};
